/**
 * Hail range and hail point from the hull (scale build SC-5a2b, plan §3.3): a station range is measured from the hull,
 * approximated by the bounding sphere (`|p − centre| − R`, R from `siteRadius`), so km-scale stations neither swallow
 * the hail range nor place a load inside their hull.
 *
 * - `hailDistance(site, x, y, z)`: the hull distance (negative inside the sphere). Allocation-free (D-014): the hail
 *   prompt runs it per station per tick.
 * - `inHailRange(site, x, y, z, rangeM)`: within `rangeM` (`hail.rangeM`, 3,000 m) of the hull. Every hail-range
 *   check goes through it: the prompt, `lastSafe.stationId` and set home, the exploration and relink commands, and the
 *   Reach panels on M2's docked screen (they run those commands from the bay pad or the moor ring).
 * - `hailPoint(site, hailPointM)`: where loads, station arrivals, the safe fallback and the home respawn place you:
 *   on the bay axis (the `DOCK_BAY_0` socket's line, out along the site's local −Z) `R + hailPointM` out from the
 *   centre's plane across that axis. The axis starts at the socket, not the centre (D-905: a bay row may sit off the
 *   centre's height), so the point is at least `hailPointM` off the hull. Rare: allocates.
 */
import { add, negZ, rotate, scale } from '../../galaxy/layout/frame'
import type { StationSite, Vec3T } from '../../galaxy/layout/types'
import { siteBayOffset, siteRadius, type RadiusSite } from './siteRadius'

/** What the hail helpers read of a station site. */
export type HailSite = RadiusSite & Pick<StationSite, 'position' | 'rotation'>

/** The distance (m) from the point to the station's hull (its bounding sphere); negative inside it. */
export function hailDistance(site: HailSite, x: number, y: number, z: number): number {
  const c = site.position
  // Not Math.hypot: V8 runs it as a builtin call (not inlined) that allocates on every call, and the hail prompt
  // calls this per station per tick (scale W2 gate: ~275 B per tick in the relink hot loop).
  const dx = c[0] - x
  const dy = c[1] - y
  const dz = c[2] - z
  return Math.sqrt(dx * dx + dy * dy + dz * dz) - siteRadius(site)
}

/** True when the point is within `rangeM` of the station's hull. */
export function inHailRange(site: HailSite, x: number, y: number, z: number, rangeM: number): boolean {
  return hailDistance(site, x, y, z) <= rangeM
}

/** The station's hail point: on its bay axis, `R + hailPointM` out from the centre's plane across the axis. */
export function hailPoint(site: HailSite, hailPointM: number): Vec3T {
  const out = negZ(site.rotation)
  const bay = rotate(site.rotation, siteBayOffset(site))
  // The bay socket's offset across the axis (its part along the axis is replaced by R + hailPointM).
  const along = bay[0] * out[0] + bay[1] * out[1] + bay[2] * out[2]
  const across = add(bay, scale(out, -along))
  return add(add(site.position, across), scale(out, siteRadius(site) + hailPointM))
}
